import {
  Invitation,
  InvitationType,
} from "@features/settings/types/invitation";
import { Button } from "@shared/components/ui/Button";
import { getApiErrorMessage } from "@shared/utils/errorHandling";
import theme from "@shared/utils/themeColors";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { useAuth } from "../hooks/useAuth";

const InvitationPage = () => {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get("id");
  const navigate = useNavigate();
  const { validateInvitation } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);

  useEffect(() => {
    // If no invitation ID is provided, redirect to sign in
    if (!invitationId) {
      navigate("/signin");
      return;
    }

    // Validate invitation
    const checkInvitation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call API to validate invitation
        const invitationData = await validateInvitation(invitationId);
        setInvitation(invitationData);

        // Kept for the signup screen: the id to register against, and the
        // address the invitation was issued to, which the backend requires the
        // new account to match.
        sessionStorage.setItem("invitationId", invitationId);
        sessionStorage.setItem("invitationEmail", invitationData.email ?? "");
      } catch (err) {
        const errorMessage = getApiErrorMessage(err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    checkInvitation();
  }, [invitationId, navigate]);

  const handleAcceptInvitation = () => {
    navigate("/signup");
  };

  const handleDeclineInvitation = () => {
    // Clear invitation data and redirect to home
    sessionStorage.removeItem("invitationId");
    sessionStorage.removeItem("invitationEmail");
    navigate("/");
  };

  return (
    <AuthLayout>
      <div className="mx-auto">
        <h1
          className={`text-3xl ${theme.text.primary} mb-6 font-petrona text-center`}
        >
          {invitation?.type === InvitationType.ADMIN
            ? "Administrator Invitation"
            : "Organization Invitation"}
        </h1>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4">Validating your invitation...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : invitation ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            {invitation.type === InvitationType.ADMIN ? (
              <>
                <p className="mb-4">
                  You've been invited to register as an administrator and create
                  your own organization.
                </p>
                <p className="mb-6">
                  To accept this invitation, you'll need to create an account
                  and complete the organization setup process.
                </p>
              </>
            ) : (
              <>
                <p className="mb-4">
                  You've been invited to join{" "}
                  <span className="font-semibold">
                    {invitation.organizationName}
                  </span>{" "}
                  as a{" "}
                  <span className="font-semibold">
                    {invitation.role || "member"}
                  </span>
                  .
                </p>
                <p className="mb-6">
                  To accept this invitation, you'll need to create an account.
                </p>
              </>
            )}
            <div className="flex justify-between mt-8">
              <Button
                variant="secondary"
                onClick={handleDeclineInvitation}
                className="mr-2"
              >
                Decline
              </Button>
              <Button variant="primary" onClick={handleAcceptInvitation}>
                Accept & Continue
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p>No invitation found. Please check your link and try again.</p>
            <Button
              variant="primary"
              onClick={() => navigate("/signin")}
              className="mt-4"
            >
              Back to Sign In
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default InvitationPage;
